import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { faChalkboardTeacher } from '@fortawesome/free-solid-svg-icons';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import { createAgent, fetchQueue, getAgents, getAgentsCount, updateAgent } from '../services/agentService';
import { getModels } from '../services/modelHubService';
import { getKnowledgeBases } from '../services/knowledgeBaseService';
import { ButtonSuccess, FormRow, SubSectionTitle, Table, TableCell, TableRow } from '../styles/PageStyles';
import { getPrompts } from '../services/promptService';
import toastService from '../services/toastService';
import Pagination from '../components/common/Pagination';
import { MultiSelectDropdownSearch } from '../components/common/MultiSelectSearch';
import { fetchMCPServer } from '../services/MCPService';

const Container = styled.div`
  padding: 2rem;
  height: 100%;
  background: #ffffff;
  color: #333333;
  position: relative;
  overflow-x: hidden;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-family: 'Orbitron', sans-serif;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  
  span {
    margin-left: 0.5rem;
    font-size: 0.9rem;
    background: rgba(0, 102, 255, 0.1);
    color: var(--primary);
    padding: 0.2rem 0.6rem;
    border-radius: 20px;
  }
`;

const Card = styled(motion.div)`
  background: #ffffff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 102, 255, 0.1);
  transition: all 0.3s ease;
  height: 100%;
  
  &:hover {
    box-shadow: 0 8px 25px rgba(0, 102, 255, 0.1);
    border-color: rgba(0, 102, 255, 0.3);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const MetricCard = styled(Card)`
  text-align: center;
  padding: 1.2rem;
`;

const MetricValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary);
  margin-bottom: 0.5rem;
  font-family: 'Orbitron', sans-serif;
`;

const MetricLabel = styled.div`
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const AgentCard = styled(Card)`
  display: flex;
  flex-direction: column;
`;

const AgentHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const AgentIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  // background: ${props => props.active ? 'var(--gradient-primary)' : 'rgba(0, 102, 255, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  font-size: 1.5rem;
  color: ${props => props.active ? '#ffffff' : 'var(--primary)'};
  font-family: 'Orbitron', sans-serif;
`;

const AgentInfo = styled.div`
  flex: 1;
`;

const AgentName = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.2rem;
`;

const AgentType = styled.div`
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const AgentDescription = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
  flex: 1;
`;

const AgentStatus = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 500;
  color: ${props => props.active ? '#10B981' : '#6B7280'};
  margin-bottom: 1rem;
  position:relative;
  &:before {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 2px;
    background: ${props => props.active ? '#10B981' : '#ececec'};
  }
`;

const AgentMetrics = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-top: 1rem;
  // border-top: 1px solid rgba(0, 102, 255, 0.1);
`;

const AgentMetric = styled.div`
  text-align: center;
`;

const AgentMetricValue = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--primary);
  margin-bottom: 0.2rem;
`;

const AgentMetricLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const Button = styled(motion.button)`
  padding: 0.8rem 1.5rem;
  background: ${props => props.primary ? 'var(--gradient-primary)' : '#ffffff'};
  border: 1px solid ${props => props.primary ? 'transparent' : 'rgba(0, 102, 255, 0.1)'};
  border-radius: 8px;
  color: ${props => props.primary ? 'white' : 'var(--text-primary)'};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${props => props.primary ? 'var(--gradient-primary)' : 'rgba(0, 102, 255, 0.03)'};
    border-color: ${props => props.primary ? 'transparent' : 'rgba(0, 102, 255, 0.2)'};
  }
  
  svg {
    margin-right: 0.5rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(0, 102, 255, 0.1);
  margin-bottom: 2rem;
`;

const Tab = styled.button`
  padding: 1rem 1.5rem;
  background: transparent;
  border: none;
  font-size: 1rem;
  font-weight: ${props => props.active ? '600' : '400'};
  color: ${props => props.active ? 'var(--primary)' : 'var(--text-secondary)'};
  cursor: pointer;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 2px;
    background: ${props => props.active ? 'var(--primary)' : 'transparent'};
  }
  
  &:hover {
    color: var(--primary);
  }
`;

const SearchBar = styled.div`
  display: flex;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.8rem 1rem;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
  }
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled(motion.div)`
  width: 900px;
  max-width: 90%;
  max-height: 90vh;
  background: #ffffff;
  border-radius: 16px;
  padding: 2rem;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 102, 255, 0.05);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 102, 255, 0.2);
    border-radius: 4px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--text-primary);
  background: var(--gradient-primary);
  font-family: 'Orbitron', sans-serif;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-secondary);
  
  &:hover {
    color: var(--text-primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem 1rem;
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(0, 102, 255, 0.1);
  }
  
  &:hover {
    border-color: rgba(0, 102, 255, 0.3);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.8rem 1rem;
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%230066FF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(0, 102, 255, 0.1);
  }
  
  &:hover {
    border-color: rgba(0, 102, 255, 0.3);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.8rem 1rem;
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
  min-height: 120px;
  resize: vertical;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(0, 102, 255, 0.1);
  }
  
  &:hover {
    border-color: rgba(0, 102, 255, 0.3);
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    top: 18px;
    left: 0;
    width: 100%;
    height: 2px;
    background: rgba(0, 102, 255, 0.1);
    z-index: 0;
  }
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 20%;
  cursor:pointer;
  z-index: 1;
`;

const StepCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${props => props.active ? 'var(--gradient-primary)' : props.completed ? 'var(--primary)' : 'rgba(0, 102, 255, 0.1)'};
  color: ${props => props.active || props.completed ? '#ffffff' : 'var(--text-secondary)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;
  box-shadow: ${props => props.active ? '0 0 15px rgba(0, 102, 255, 0.3)' : 'none'};
`;

const StepLabel = styled.div`
  font-size: 0.8rem;
  color: ${props => props.active ? 'var(--primary)' : 'var(--text-secondary)'};
  font-weight: ${props => props.active ? '600' : '400'};
  text-align: center;
  transition: all 0.3s ease;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SelectableCard = styled.div`
  padding: 1rem;
  border-radius: 8px;
  border: 2px solid ${props => props.selected ? 'var(--primary)' : 'rgba(0, 102, 255, 0.1)'};
  background: ${props => props.selected ? 'rgba(0, 102, 255, 0.05)' : '#ffffff'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  
  &:hover {
    border-color: var(--primary);
    transform: translateY(-2px);
  }
`;

const CardIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.5rem;
  color: ${props => props.selected ? 'var(--primary)' : 'var(--text-secondary)'};
`;

const CardTitle = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.3rem;
  color: ${props => props.selected ? 'var(--primary)' : 'var(--text-primary)'};
`;

const CardDescription = styled.div`
  font-size: 0.8rem;
  color: var(--text-secondary);
`;

const ToggleSwitch = styled.div`
  width: 40px;
  height: 20px;
  background: ${props => props.on ? 'var(--primary)' : 'rgba(0, 102, 255, 0.1)'};
  border-radius: 10px;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:after {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    background: #ffffff;
    border-radius: 50%;
    top: 2px;
    left: ${props => props.on ? 'calc(100% - 18px)' : '2px'};
    transition: all 0.3s ease;
  }
`;

const Slider = styled.input`
  -webkit-appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: rgba(0, 102, 255, 0.1);
  outline: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--primary);
    cursor: pointer;
  }
`;

const Tag = styled.div`
  padding: 0.5rem 1rem;
  background: rgba(0, 102, 255, 0.1);
  border-radius: 20px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
  
  span {
    cursor: pointer;
    color: var(--text-secondary);
    
    &:hover {
      color: var(--text-primary);
    }
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const UploadArea = styled.div`
  border: 2px dashed rgba(0, 102, 255, 0.3);
  border-radius: 8px;
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 102, 255, 0.02);
    border-color: var(--primary);
  }
`;

const UploadIcon = styled.div`
  font-size: 2rem;
  color: var(--primary);
  margin-bottom: 1rem;
`;

const InfoBox = styled.div`
  background: rgba(0, 102, 255, 0.05);
  border-left: 3px solid var(--primary);
  padding: 1rem;
  border-radius: 0 8px 8px 0;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const TabsContainer = styled.div`
  display: flex;
  // border-bottom: 1px solid rgba(0, 102, 255, 0.1);
  margin-bottom: 1.5rem;
`;

const ModalTab = styled.button`
  padding: 0.8rem 1.2rem;
  background: transparent;
  border: none;
  font-size: 0.9rem;
  font-weight: ${props => props.active ? '600' : '400'};
  color: ${props => props.active ? 'var(--primary)' : 'var(--text-secondary)'};
  cursor: pointer;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 2px;
    background: ${props => props.active ? 'var(--primary)' : 'transparent'};
    transition: all 0.2s ease;
  }
  
  &:hover {
    color: var(--primary);
  }
`;

const CodeEditor = styled.div`
  font-family: 'Roboto Mono', monospace;
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  line-height: 1.5;
  overflow-x: auto;
  margin-bottom: 1.5rem;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(0, 102, 255, 0.1);
`;

const AgentStudio = () => {
  const [activeTab, setActiveTab] = useState('active');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedAgent, setSelectedAgent] = useState();
  const [currentSetupStep, setCurrentSetupStep] = useState(1);

  const [agentName, setAgentName] = useState('');
  const [agentType, setAgentType] = useState('');
  const [agentDescription, setAgentDescription] = useState('');
  const [businessDomain, setBusinessDomain] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(2000);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState([]);
  const [selectedDataSources, setSelectedDataSources] = useState([]);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [exampleConversations, setExampleConversations] = useState([]);
  const [securityPolicies, setSecurityPolicies] = useState([]);
  const [accessLevel, setAccessLevel] = useState('standard');
  const [approvalWorkflow, setApprovalWorkflow] = useState('none');
  const [modalTab, setModalTab] = useState('general');
  const [modalTabSecurity, setModalTabSecurity] = useState('security');
  const [modalTabPrompt, setModalTabPrompt] = useState('system');
  const [agentCapabilities, setAgentCapabilities] = useState([]);
  const [newTagInput, setNewTagInput] = useState('');
  const [agentTags, setAgentTags] = useState([]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showPromptTips, setShowPromptTips] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [templates, setTemplates] = useState({
    greeting: 'Hello! How can I assist you today?',
    error: 'I apologize, but I encountered an error. Could you please try again?',
    clarification: 'Could you please provide more details about your request?',
    confirmation: 'I understand your request. Would you like me to proceed?',
    custom: ''
  });

  const [agentIcon, setAgentIcon] = useState('/assets/images/agents/agent.png');

  const [editingExampleIndex, setEditingExampleIndex] = useState(-1);
  const [currentExampleMessages, setCurrentExampleMessages] = useState([]);
  const [showExampleEditor, setShowExampleEditor] = useState(false);
  const [defaultSystemPrompts, setDefaultSystemPrompts] = useState({});
  const [defaultTemplates, setDefaultTemplates] = useState({});
  const [savedTemplates, setSavedTemplates] = useState({});

  const [authMethod, setAuthMethod] = useState('');
  const [enableRateLimiting, setEnableRateLimiting] = useState(false);
  const [rateLimit, setRateLimit] = useState(100);
  const [burstLimit, setBurstLimit] = useState(10);
  const [contentFilters, setContentFilters] = useState({
    profanity: true,
    sensitive_topics: true,
    personal_info: true,
    hate_speech: true
  });
  const [moderationLevel, setModerationLevel] = useState(3);
  const [blockedTopics, setBlockedTopics] = useState([]);
  const [newBlockedTopic, setNewBlockedTopic] = useState('');
  const [userGroups, setUserGroups] = useState([]);
  const [newUserGroup, setNewUserGroup] = useState('');
  const [dataPermissions, setDataPermissions] = useState({
    read_user_data: false,
    write_user_data: false,
    read_system_data: false,
    write_system_data: false
  });
  const [auditLogLevel, setAuditLogLevel] = useState('standard');

  const [deploymentEnvironment, setDeploymentEnvironment] = useState('');
  const [deploymentType, setDeploymentType] = useState('');
  const [deploymentSchedule, setDeploymentSchedule] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [isCreatingAgent, setIsCreatingAgent] = useState(false);
  const [agentList, setAgentList] = useState([]);
  const [modelList, setModelList] = useState([]);
  const [kbList, setKbList] = useState([]);
  const [promptList, setPromptList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [workingAgents, setWorkingAgents] = useState([]);
  const [agentsCount, setAgentsCount] = useState(null)
  const [mcpToolsData, setMcpToolsData] = useState(null);
  const [selectedMCPServer, setSelectedMCPServer] = useState(null)


  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const defaultAgentIcon = "assets/images/agents/agent.png"


  useEffect((e) => {
    const fetchWorkingAgentList = async () => {
      try {
        const resp = await fetchQueue();
        setWorkingAgents(resp);
      } catch (error) {
        console.log("error queue:", error)
      }
    }

    const fetchModels = async () => {
      try {
        const resp = await getModels();
        setModelList(resp || []);
      } catch (error) {
        console.log("error queue:", error)
      }
    }
    const fetchKnowledgeBases = async () => {
      try {
        const resp = await getKnowledgeBases();
        setKbList(resp || []);
      } catch (error) {
        console.log("error queue:", error)
      }
    }
    const getPromptsList = async () => {
      try {
        const resp = await getPrompts();
        setPromptList(resp || []);
      } catch (error) {
        console.log("error queue:", error)
      }
    }

    const getAllMCPServers = async () => {
      try {
        const data = await fetchMCPServer();
        setMcpToolsData(data);
        let mcpServerList = data?.server_indexed_mcp_tools && Object.keys(data?.server_indexed_mcp_tools);
        setSelectedMCPServer(mcpServerList[0]);
      } catch (error) {
        console.log("something went wrong while fetching the mcp tools")
      }
      
    }


    fetchWorkingAgentList();
    fetchModels();
    fetchKnowledgeBases();
    getPromptsList();
    getAllMCPServers();
  }, [])


  useEffect(() => {
    const fetchAgentsList = async () => {
      try {
        const queryParams = `skip=${(currentPage - 1) * itemsPerPage}&limit=${itemsPerPage}`;
        const resp = await getAgents({}, {}, queryParams);
        setAgentList(resp);
      } catch (error) {
        console.log("error queue:", error)
      }
    }

    fetchAgentsList();
  }, [currentPage])

  useEffect(() => {
    const fetchAgetCount = async () => {
      try {
        const count = await getAgentsCount();
        setTotalItems(count);
        setAgentsCount(count);
      } catch (error) {
        console.log("Error while fetching agent count", error);
      }
    }

    fetchAgetCount();
  }, [])



  const openModal = (type, agent = null) => {
    setModalType(type);
    setSelectedAgent(agent || { ...initialAgent });
    setShowModal(true);
    setCurrentSetupStep(1);
    
    if (agent) {
      setModerationLevel(agent.moderation_level || 3);
      setAgentName(agent.name || '');
      setAgentType(agent.type || '');
      setSystemPrompt(agent.prompt.system_prompt || '');
      setAgentDescription(agent.description || '');
      setAgentTags(agent.tags || []);
      setAgentIcon(agent.icon || '/assets/images/agents/agent.png');
    } else {
      setAgentName('');
      setAgentType('');
      setAgentDescription('');
      setBusinessDomain('');
      setSelectedModel('');
      setTemperature(0.7);
      setMaxTokens(2000);
      setSelectedKnowledgeBase([]);
      setSelectedDataSources([]);
      setSystemPrompt('');
      setExampleConversations([]);
      setSecurityPolicies([]);
      setAccessLevel('standard');
      setApprovalWorkflow('none');
      setAgentCapabilities([]);
      setAgentIcon('/assets/images/agents/agent.png');
      setAgentTags([]);
      setShowAdvancedOptions(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setAgentName('');
    setAgentType('');
    setAgentDescription('');
    setBusinessDomain('');
    setSelectedModel('');
    setTemperature(0.7);
    setMaxTokens(2000);
    setSelectedKnowledgeBase([]);
    setSelectedDataSources([]);
    setSystemPrompt('');
    setExampleConversations([]);
    setSecurityPolicies([]);
    setAccessLevel('standard');
    setApprovalWorkflow('none');
    setAgentCapabilities([]);
    setAgentIcon('/assets/images/agents/agent.png');
    setAgentTags([]);
    setShowAdvancedOptions(false);
  };

  const nextStep = () => {
    setCurrentSetupStep(prev => prev + 1);
  };

  const prevStep = () => {
    setCurrentSetupStep(prev => Math.max(1, prev - 1));
  };

  const handleRemoveTag = (tag) => {
    setAgentTags(agentTags.filter(t => t !== tag));
  };

  const handleToggleCapability = (capability) => {
    if (agentCapabilities.includes(capability)) {
      setAgentCapabilities(agentCapabilities.filter(c => c !== capability));
    } else {
      setAgentCapabilities([...agentCapabilities, capability]);
    }
  };



  // Add Example
  const handleAddExampleConversation = () => {
    // setSelectedAgent((prev) => ({
    //   ...prev,
    //   prompt: {
    //     ...prev.prompt,
    //     example_conversation: [
    //       ...prev.prompt.example_conversation,
    //       { title: "", conversations: [] }
    //     ]
    //   }
    // }));
    setEditingExampleIndex(0);
    setCurrentExampleMessages({
      title: "",
      conversations: []
    })
    setShowExampleEditor(true);
  };






  const handleUpdateExampleConversation = (id, field, value) => {
    setExampleConversations(
      exampleConversations.map(ex =>
        ex.id === id ? { ...ex, [field]: value } : ex
      )
    );
  };

  const handleRemoveExampleConversation = (id) => {
    setExampleConversations(exampleConversations.filter(ex => ex.id !== id));
  };

  const handleToggleSecurityPolicy = (policy) => {
    if (securityPolicies.includes(policy)) {
      setSecurityPolicies(securityPolicies.filter(p => p !== policy));
    } else {
      setSecurityPolicies([...securityPolicies, policy]);
    }
  };

  const handleAddKnowledgeBase = (kb) => {
    if (!selectedKnowledgeBase.includes(kb)) {
      setSelectedKnowledgeBase([...selectedKnowledgeBase, kb]);
    }
  };

  const handleRemoveKnowledgeBase = (kb) => {
    setSelectedKnowledgeBase(selectedKnowledgeBase.filter(k => k !== kb));
  };

  const handleAddDataSource = (source) => {
    if (!selectedDataSources.includes(source)) {
      setSelectedDataSources([...selectedDataSources, source]);
    }
  };

  const handleRemoveDataSource = (source) => {
    setSelectedDataSources(selectedDataSources.filter(s => s !== source));
  };

  const handleSelectIcon = (icon) => {
    setAgentIcon(icon);
  };
const filteredAgentList = agentList.filter(agent => {
  if (!searchQuery.trim()) return true;
  
  // Case-insensitive search through name, type, and description
  const query = searchQuery.toLowerCase();
  return (
    agent.name.toLowerCase().includes(query) ||
    agent.type.toLowerCase().includes(query) ||
    agent.description.toLowerCase().includes(query) ||
    (agent.tags && agent.tags.some(tag => tag.toLowerCase().includes(query)))
  );
});

  const handleDeployAgent = () => {
    closeModal();
  };
  const initialAgent = {
    id: "",
    code: "AG-" + new Date().getTime(),
    name: "",
    status: "ACTIVE",
    type: "",
    business_domain: "",
    icon: "",
    tags: [],
    description: "",
    capabilities: [],
    general_settings: {
      model_code: "",
      model_temperature: 0,
      max_tokens: 1000,
    },
    advanced_settings: {
      hosted_on: "",
      caching_strategy: "",
      top_p: 0.5,
      frequency_penalty: 0.5,
      presence_penalty: 1,
      response_format: 'auto'
    },
    knowledge_base: {
      knowledgebase_code: "",
      vector_database: "",
      embedding_model: ""
    },
    prompt: {
      system_prompt: "",
      example_conversation: [],
      response_template:
      {
        template_type: "",
        template_name: "",
        template_content: ""
      }

    },
    security: {
      security_settings: {
        data_encryption: false,
        pii_handling: false,
        audit_loggin: false,
        input_validation: false,
        rate_limiting: false,
        authentication_method: "",
        api_rate_limiting: false,
        api_limit_requests_per_minute: 100,
        api_limit_burst_limit: 10
      },
      content_guardrails: {
        profanity_filter: true,
        pii_detection: false,
        harmful_content: false,
        misinformation: false,
        bias_detection: false,
        content_moderation: 3,
        custom_topics_to_block: []
      },
      access_control: {
        access_level: "",
        read_user_data: false,
        write_user_data: false,
        read_system_data: false,
        write_system_data: false,
        audit_logging: ""
      }
    },
    deployment: {
      deployment_environment_option: "",
      deployment_schedule: ""
    },
    mcp_servers : {}
  }

  const templateAgents = [
    {
      id: 101,
      name: 'Fraud Waste Abuse Identification Agent for Health Insurance',
      type: 'Template',
      description: 'Pre-configured to identify suspicious patterns and anomalies in health insurance claims that may indicate fraud, waste, or abuse.',
      icon: '🛡️',
      template: true
    },
    {
      id: 102,
      name: 'Provider Onboarding and Credentialling Agent',
      type: 'Template',
      description: 'Streamlines the process of onboarding new healthcare providers and verifying their credentials and qualifications.',
      icon: '👨‍⚕️',
      template: true
    },
    {
      id: 103,
      name: 'Tariff Management Agent for Provider',
      type: 'Template',
      description: 'Manages and updates provider tariffs and fee schedules, ensuring accurate pricing and billing for medical services.',
      icon: '💰',
      template: true
    },
    {
      id: 104,
      name: 'Benefit Configurator Agent',
      type: 'Template',
      description: 'Helps configure and customize insurance benefit packages based on client requirements and regulatory guidelines.',
      icon: '⚙️',
      template: true
    },
    {
      id: 105,
      name: 'Benefit Simulator Agent',
      type: 'Template',
      description: 'Simulates different benefit scenarios to help clients understand coverage options and make informed decisions.',
      icon: '📊',
      template: true
    }
  ];

  const metrics = [
    { label: 'Active Agents', value: agentsCount },
    { label: 'Tasks in Progress', value: workingAgents.length },
    { label: 'Tasks Completed', value: 127 },
    { label: 'Avg. Accuracy', value: '96%' }
  ];

  const updateSelectedAgent = (path, value) => {
    setSelectedAgent((prev) => {
      const updatedAgent = { ...prev }; // Create a shallow copy of selectedAgent
      const keys = path.split('.'); // Split the path into keys (e.g., "object.key" -> ["object", "key"])

      let current = updatedAgent; // Start at the root of the object

      keys.forEach((key, index) => {
        if (key.includes('[')) {
          // Handle array indexing, e.g., "array[0].key"
          const [arrayKey, arrayIndex] = key.match(/([^\[\]]+)|(\d+)/g); // Extract array name and index
          if (index === keys.length - 1) {
            // If it's the last key, update the value
            current[arrayKey][parseInt(arrayIndex, 10)] = {
              ...current[arrayKey][parseInt(arrayIndex, 10)],
              [keys[index + 1]]: value,
            };
          } else {
            current = current[arrayKey][parseInt(arrayIndex, 10)];
          }
        } else if (index === keys.length - 1) {
          // If it's the last key, update the value
          current[key] = value;
        } else {
          // Otherwise, move deeper into the object
          current = current[key];
        }
      });
      console.log(updatedAgent)
      return updatedAgent;
    });
  };
  const renderAgentSetupModal = () => {
    const steps = ['Basic Info', 'Model & Knowledge', 'Prompts', 'Security & Guardrails', 'Review'];
    const errors = validateAgent();

    const handleFieldChange = (field, value) => {
      setSelectedAgent((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

    const handleNestedFieldChange = (parentField, field, value) => {
      setSelectedAgent((prev) => ({
        ...prev,
        [parentField]: {
          ...prev[parentField],
          [field]: value,
        },
      }));
    };

    const handleArrayFieldChange = (field, value) => {
      setSelectedAgent((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

    const handleSubmit = async () => {
      // console.log('Final Agent Data:', selectedAgent);
      if (selectedAgent.id) {
        try {
          const resp = await updateAgent(selectedAgent)
          // Update the agent in the agentList
          setAgentList(prevList =>
            prevList.map(agent =>
              agent.id === selectedAgent.id ? resp : agent
            )
          );
          toastService.success("Agent Updated Successfully")
        } catch (error) {
          toastService.error("Failed to update agent");
        }
      } else {
        try {
          const resp = await createAgent(selectedAgent)
          setAgentList(prevList => [...prevList, resp]);
      
          toastService.success("Agent Created Successfully")
        } catch (error) {
          toastService.error("Failed to create agent");
        }
      }
      // Call API to save the agent
      closeModal();
    };

    const handleUpdateExampleMessage = (index, field, value) => {
      setCurrentExampleMessages((prev) => {
        const updatedConversations = [...prev.conversations];
        updatedConversations[index] = {
          ...updatedConversations[index],
          [field]: value,
        };
        return {
          ...prev,
          conversations: updatedConversations,
        };
      });
    };

    const iconPaths = [
      'assets/images/agents/agent.png',
      'assets/images/agents/clipBoard.png',
      'assets/images/agents/config.png',
      'assets/images/agents/health-insurance.png',
      'assets/images/agents/hospital.png',
      'assets/images/agents/insurance.png',
      'assets/images/agents/money-bag.png',
      'assets/images/agents/search.png',
      'assets/images/agents/shield.png',
    ]

    return (
      <ModalContent
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <ModalHeader>
          <ModalTitle>{selectedAgent ? 'Edit Agent' : 'Design New Agent'}</ModalTitle>
          <CloseButton onClick={closeModal}>×</CloseButton>
        </ModalHeader>

        {/* Progress Indicator */}
        <StepIndicator>
          {steps.map((step, index) => (
            <Step key={index}>
              <StepCircle
                active={currentSetupStep === index + 1}
                completed={currentSetupStep > index + 1}
                onClick={() => setCurrentSetupStep(index + 1)}
              >
                {currentSetupStep > index + 1 ? '✓' : index + 1}
              </StepCircle>
              <StepLabel active={currentSetupStep === index + 1}>{step}</StepLabel>
            </Step>
          ))}
        </StepIndicator>

        {/* Step 1: Basic Info */}
        {currentSetupStep === 1 && (
          <>
            <InfoBox>
              Start by defining the basic information about your agent. A well-defined agent with clear purpose and scope will perform better.
            </InfoBox>
            <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div style={{ flex: '1' }}>
                <FormGroup>
                  <Label>Agent Name*</Label>
                  <Input
                    type="text"
                    placeholder="Enter agent name"
                    value={selectedAgent.name || ''}
                    onChange={(e) => updateSelectedAgent('name', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Agent Type*</Label>
                  <Select
                    value={selectedAgent.type || ''}
                    onChange={(e) => updateSelectedAgent('type', e.target.value)}
                  >
                    <option value="">Select agent type</option>
                    <option value="Insurance">Insurance</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Claims Processing">Claims Processing</option>
                    <option value="Underwriting">Underwriting</option>
                    <option value="Fraud Detection">Fraud Detection</option>
                    <option value="Provider Management">Provider Management</option>
                    <option value="Benefits">Benefits</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Business Domain*</Label>
                  <Select
                    value={selectedAgent.business_domain || ''}
                    onChange={(e) => updateSelectedAgent('business_domain', e.target.value)}
                  >
                    <option value="">Select business domain</option>
                    <option value="life_insurance">Life Insurance</option>
                    <option value="health_insurance">Health Insurance</option>
                    <option value="property_insurance">Property Insurance</option>
                    <option value="auto_insurance">Auto Insurance</option>
                    <option value="provider_network">Provider Network</option>
                    <option value="benefits_management">Benefits Management</option>
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Tags</Label>
                  <div style={{ display: 'flex', marginBottom: '0.5rem' }}>
                    <Input
                      type="text"
                      placeholder="Add tag"
                      value={newTagInput}
                      onChange={e => setNewTagInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && newTagInput.trim()) {
                          setAgentTags(prev => [...prev, newTagInput.trim()]);
                          setSelectedAgent(prev => ({
                            ...prev,
                            tags: [...(prev.tags || []), newTagInput.trim()]
                          }));
                          setNewTagInput('');
                        }
                      }}
                      style={{ flex: '1', marginRight: '0.5rem' }}
                    />
                    <Button
                      onClick={() => {
                        if (newTagInput.trim()) {
                          setAgentTags(prev => [...prev, newTagInput.trim()]);
                          setSelectedAgent(prev => ({
                            ...prev,
                            tags: [...(prev.tags || []), newTagInput.trim()]
                          }));
                          setNewTagInput('');
                        }
                      }}
                    >Add</Button>
                  </div>

                  <TagsContainer>
                    {agentTags.map(tag => (
                      <Tag key={tag}>
                        {tag}
                        <span onClick={() => handleRemoveTag(tag)}>×</span>
                      </Tag>
                    ))}
                  </TagsContainer>
                </FormGroup>
              </div>
              <div style={{ flex: '1' }}>
                <FormGroup>
                  <Label style={{textAlign:'center'}}>Agent Icon</Label>
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '16px',
                      border: '1px solid #0066FF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1rem'
                    }}>
                      <img
                        src={selectedAgent.icon || defaultAgentIcon}
                        alt="Selected Icon"
                        style={{ width: '30px', height: '30px', objectFit: 'contain' }}
                      />
                    </div>
                  </div>

                  <CardGrid style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))' }}>
                    {iconPaths.map((iconPath) => (
                      <SelectableCard
                        key={iconPath}
                        selected={selectedAgent.icon === iconPath}
                        onClick={() => {
                          updateSelectedAgent("icon", iconPath)
                        }}
                        style={{ padding: '0.5rem' }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50px' }}>
                          <img
                            src={iconPath}
                            alt="Agent Icon"
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        </div>
                      </SelectableCard>
                    ))}
                  </CardGrid>
                </FormGroup>


              </div>

            </div>

            <FormGroup>
              <Label>Description*</Label>
              <Textarea
                placeholder="Describe what this agent does"
                value={selectedAgent.description || ''}
                onChange={(e) => updateSelectedAgent('description', e.target.value)}
              />
            </FormGroup>
          </>
        )}

        {/* Step 2: Model & Knowledge */}
        {currentSetupStep === 2 && (
          <>
            <InfoBox>
              Configure the AI model and knowledge sources that will power your agent. The right combination will significantly impact performance.

            </InfoBox>

            <TabsContainer>
              <ModalTab
                active={modalTab === 'general'}
                onClick={() => setModalTab('general')}
              >
                General Settings
              </ModalTab>
              <ModalTab
                active={modalTab === 'advanced'}
                onClick={() => setModalTab('advanced')}
              >
                Advanced Settings
              </ModalTab>
              <ModalTab
                active={modalTab === 'knowledge'}
                onClick={() => setModalTab('knowledge')}
              >
                Knowledge Base
              </ModalTab>
                 <ModalTab
                    active={modalTab === 'MCP Tools'}
                    onClick={() => setModalTab('MCP Tools')}
                     >
                    MCP Tools
                </ModalTab>
            </TabsContainer>
            {modalTab === 'general' && (
              <>
                <FormGroup>
                  <Label>Source</Label>
                  {/* <FormGroup>
                    <Label>Source</Label>
                    <Select
                      value={selectedAgent.general_settings.source || ""}
                      onChange={(e) => updateSelectedAgent('general_settings.source', e.target.value)}
                    >
                      <option value="">Select source</option>
                      <option value="azure-openai">OpenAI</option>
                      <option value="anthropic">Anthropic</option>
                      <option value="google">Google</option>
                    </Select>
                  </FormGroup> */}

                     {/* <FormGroup>
                    <Label>Model Code</Label>
                    <Select
                      value={selectedAgent.general_settings.model_code || ""}
                      onChange={(e) => updateSelectedAgent('general_settings.model_code', e.target.value)}
                    >
                      <option value="">Select source</option>
                      <option value="gpt-4o">OpenAI</option>
                      <option value="claude-sonnet-4-20250514">Anthropic</option>
                      <option value="gemini-2.5-pro-preview-03-25">Google</option>
                    </Select>
                  </FormGroup> */}
                
                  <CardGrid>
                    {modelList.map((model, index) => (
                      <SelectableCard
                        key={index}
                        selected={selectedAgent.general_settings.model_code === model.code}
                        onClick={() => {
                          updateSelectedAgent('general_settings.model_code', model.code)
                        }}
                      >
                        <CardIcon selected={selectedAgent.general_settings.model_code === model.code}><img src={`assets/images/models/${model.icon}`} alt={model.name} width={70} height={70} /></CardIcon>
                        <CardTitle selected={selectedAgent.general_settings.model_code === model.code}>{model.name}</CardTitle>
                        <CardDescription>{model.description}</CardDescription>
                      </SelectableCard>
                    ))}
                  </CardGrid>
                </FormGroup>

                <FormGroup>
                  <Label>Model Parameters</Label>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '1.5rem',
                    background: 'rgba(0, 102, 255, 0.02)',
                    padding: '1.5rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 102, 255, 0.1)'
                  }}>
                    <div>
                      <Label style={{ fontSize: '0.9rem', display: 'flex', justifyContent: 'space-between' }}>
                        <span>Temperature</span>
                        <span style={{ color: 'var(--primary)' }}>{selectedAgent.general_settings.model_temperature}</span>
                      </Label>
                      <Slider
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={selectedAgent.general_settings.model_temperature}
                        onChange={e => {
                          updateSelectedAgent('general_settings.model_temperature', parseFloat(e.target.value))
                        }}
                      />
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.8rem',
                        color: 'var(--text-secondary)',
                        marginTop: '0.5rem'
                      }}>
                        <span>Precise (0)</span>
                        <span>Creative (1)</span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                        Lower values produce more predictable outputs, higher values more creative ones
                      </div>
                    </div>
                    <div>
                      <Label style={{ fontSize: '0.9rem', display: 'flex', justifyContent: 'space-between' }}>
                        <span>Max Tokens</span>
                        <span style={{ color: 'var(--primary)' }}>{selectedAgent.general_settings.max_tokens}</span>
                      </Label>
                      <Slider
                        type="range"
                        min="1000"
                        max="8000"
                        step="500"
                        value={selectedAgent.general_settings.max_tokens}
                        onChange={e => updateSelectedAgent('general_settings.max_tokens', parseFloat(e.target.value))}
                      />
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.8rem',
                        color: 'var(--text-secondary)',
                        marginTop: '0.5rem'
                      }}>
                        <span>1,000</span>
                        <span>8,000</span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                        Maximum length of the model's response (higher values may increase costs)
                      </div>
                    </div>
                  </div>
                </FormGroup>
              </>
            )}

            {modalTab === 'advanced' && (
              <>
                <FormGroup>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <Label style={{ margin: 0 }}>Advanced Model Settings</Label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Show Advanced</span>
                      <ToggleSwitch
                        on={showAdvancedOptions}
                        onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                      />
                    </div>
                  </div>

                  {showAdvancedOptions && (
                    <div style={{
                      background: 'rgba(0, 102, 255, 0.02)',
                      padding: '1.5rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(0, 102, 255, 0.1)'
                    }}>
                      <div>
                        <Label style={{ fontSize: '0.9rem', display: 'flex', justifyContent: 'space-between' }}>
                          <span>Top P</span>
                          <span style={{ color: 'var(--primary)' }}>{selectedAgent.advanced_settings.top_p}</span>
                        </Label>
                        <Slider type="range" min="0.1" max="1" step="0.1" defaultValue="0.9"

                          value={selectedAgent.advanced_settings.top_p}
                          onChange={e => updateSelectedAgent('advanced_settings.top_p', parseFloat(e.target.value))}
                        />
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '0.8rem',
                          color: 'var(--text-secondary)',
                          marginTop: '0.5rem'
                        }}
                        >
                          <span>0.1</span>
                          <span>1</span>
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                          Controls diversity via nucleus sampling
                        </div>
                      </div>
                      <div>
                        <Label style={{ fontSize: '0.9rem', display: 'flex', justifyContent: 'space-between' }}>
                          <span>Frequency Penalty</span>
                          <span style={{ color: 'var(--primary)' }}>{selectedAgent.advanced_settings.frequency_penalty}</span>
                        </Label>
                        <Slider type="range" min="0" max="2" step="0.1" defaultValue="0.5"
                          value={selectedAgent.advanced_settings.frequency_penalty}
                          onChange={e => updateSelectedAgent('advanced_settings.frequency_penalty', parseFloat(e.target.value))}
                        />
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '0.8rem',
                          color: 'var(--text-secondary)',
                          marginTop: '0.5rem'
                        }}>
                          <span>0</span>
                          <span>2</span>
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                          Reduces repetition of the same phrases
                        </div>
                      </div>
                      <div>
                        <Label style={{ fontSize: '0.9rem', display: 'flex', justifyContent: 'space-between' }}>
                          <span>Presence Penalty</span>
                          <span style={{ color: 'var(--primary)' }}>{selectedAgent.advanced_settings.presence_penalty}</span>
                        </Label>
                        <Slider type="range" min="0" max="2" step="0.1" defaultValue="0.0"
                          value={selectedAgent.advanced_settings.presence_penalty}
                          onChange={e => updateSelectedAgent('advanced_settings.presence_penalty', parseFloat(e.target.value))}
                        />
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '0.8rem',
                          color: 'var(--text-secondary)',
                          marginTop: '0.5rem'
                        }}>
                          <span>0</span>
                          <span>2</span>
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                          Encourages talking about new topics
                        </div>
                      </div>
                      <div>
                        <Label style={{ fontSize: '0.9rem' }}>Response Format</Label>
                        <Select defaultValue="auto"
                          value={selectedAgent.advanced_settings.response_format}
                          onChange={e => updateSelectedAgent('advanced_settings.response_format', e.target.value)}
                        >
                          <option value="auto">Auto</option>
                          <option value="text">Text</option>
                          <option value="json">JSON</option>
                        </Select>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                          Specify the format of model outputs
                        </div>
                      </div>
                    </div>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Model Hosting</Label>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '1rem'
                  }}>
                    <SelectableCard
                      selected={selectedAgent.advanced_settings.hosted_on === 'cloud'}
                      onClick={() => {
                        updateSelectedAgent('advanced_settings.hosted_on', 'cloud')
                      }}
                    >
                      <CardIcon selected={selectedAgent.advanced_settings.hosted_on === 'cloud'}>☁️</CardIcon>
                      <CardTitle selected={selectedAgent.advanced_settings.hosted_on === 'cloud'}>Cloud Hosted</CardTitle>
                      <CardDescription>Use our managed cloud infrastructure for easy deployment</CardDescription>
                    </SelectableCard>
                    <SelectableCard
                      selected={selectedAgent.advanced_settings.hosted_on === 'onprem'}
                      onClick={() => {
                        updateSelectedAgent('advanced_settings.hosted_on', 'onprem')
                      }}
                    >
                      <CardIcon selected={selectedAgent.advanced_settings.hosted_on === 'onprem'}>🏢</CardIcon>
                      <CardTitle selected={selectedAgent.advanced_settings.hosted_on === 'onprem'}>On-Premises</CardTitle>
                      <CardDescription>Deploy on your own infrastructure for maximum control</CardDescription>
                    </SelectableCard>
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>Caching Strategy</Label>
                  <Select defaultValue="dynamic" onChange={(e) =>
                    updateSelectedAgent('advanced_settings.caching_strategy', e.target.value)
                  }>
                    <option value="none">No Caching</option>
                    <option value="static">Static (Cache all responses)</option>
                    <option value="dynamic">Dynamic (Smart caching)</option>
                  </Select>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                    Caching can improve response times and reduce costs for repetitive queries
                  </div>
                </FormGroup>
              </>
            )}

            {modalTab === 'knowledge' && (
              <>
                <FormGroup>
                  <Label>Knowledge Base</Label>
                  {/* <div style={{ marginBottom: '1rem' }}>
                    <TagsContainer>
                      {selectedKnowledgeBase.map(kb => (
                        <Tag key={kb}>
                          {kb}
                          <span onClick={() => handleRemoveKnowledgeBase(kb)}>×</span>
                        </Tag>
                      ))}
                    </TagsContainer>

                    <Select
                      value=""
                      onChange={e => {
                        if (e.target.value) handleAddKnowledgeBase(e.target.value);
                      }}
                    >
                      <option value="">Add knowledge base</option>
                      {kbList.map((kb,index) => (
                        <option value={kb.code}>{kb.name}</option>
                      ))}
                    </Select>
                  </div> */}
                  <div style={{ marginBottom: '1rem' }}>

                    <Select
                      value={selectedAgent.knowledge_base.knowledgebase_code}
                      onChange={(e) =>
                        updateSelectedAgent('knowledge_base.knowledgebase_code', e.target.value)
                      }
                    >
                      <option value="">Add knowledge base</option>
                      {kbList.map((kb, index) => (
                        <option value={kb.code}>{kb.name}</option>
                      ))}
                    </Select>
                  </div>


                  {/* <UploadArea>
                    <UploadIcon>📤</UploadIcon>
                    <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>Upload Custom Knowledge Base</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      Supports PDF, DOCX, CSV, JSON (Max 100MB)
                    </div>
                  </UploadArea> */}
                </FormGroup>

                {/* <FormGroup>
                  <Label>Data Sources</Label>
                  <div style={{ marginBottom: '1rem' }}>
                    <TagsContainer>
                      {selectedDataSources.map(source => (
                        <Tag key={source}>
                          {source}
                          <span onClick={() => handleRemoveDataSource(source)}>×</span>
                        </Tag>
                      ))}
                    </TagsContainer>

                    <Select
                      value=""
                      onChange={e => {
                        if (e.target.value) handleAddDataSource(e.target.value);
                      }}
                    >
                      <option value="">Add data source</option>
                      <option value="Policy Database">Policy Database</option>
                      <option value="Claims History">Claims History</option>
                      <option value="Provider Directory">Provider Directory</option>
                      <option value="Regulatory Updates">Regulatory Updates</option>
                      <option value="Customer Records">Customer Records</option>
                    </Select>
                  </div>
                </FormGroup> */}

                <FormGroup>
                  <Label>Vector Database</Label>
                  <Select defaultValue="pinecone"
                    value={selectedAgent.knowledge_base.vector_database}
                    onChange={(e) =>
                      updateSelectedAgent('knowledge_base.vector_database', e.target.value)
                    }
                  >
                    <option value="pinecone">Pinecone</option>
                    <option value="qdrant">Qdrant</option>
                    <option value="weaviate">Weaviate</option>
                    <option value="redis">Redis</option>
                    <option value="custom">Custom</option>
                  </Select>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                    Vector database used for semantic search and retrieval
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>Embedding Model</Label>
                  <Select defaultValue="openai"
                    value={selectedAgent.knowledge_base.embedding_model}
                    onChange={(e) =>
                      updateSelectedAgent('knowledge_base.embedding_model', e.target.value)
                    }>
                    <option value="openai">OpenAI Embeddings</option>
                    <option value="cohere">Cohere Embeddings</option>
                    <option value="sentence-transformers">Sentence Transformers</option>
                    <option value="custom">Custom</option>
                  </Select>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                    Model used to convert text into vector embeddings
                  </div>
                </FormGroup>
              </>
            )}
            

            {modalTab === 'MCP Tools' && (
              <>
                {(() => {
                  const selectedForThisServer =
                    selectedAgent?.mcp_servers?.[selectedMCPServer] || [];
                  const tools = mcpToolsData?.server_indexed_mcp_tools[selectedMCPServer]
                  const serverList = mcpToolsData?.server_indexed_mcp_tools && Object.keys(mcpToolsData?.server_indexed_mcp_tools);
                  return (
                    <>
                      <Select value={selectedMCPServer} onChange={(e) => setSelectedMCPServer(e.target.value)}>
                        {
                          serverList?.length > 0 && serverList.map((server, index) => (
                            <option key={index}>{server}</option>
                          ))
                        }

                      </Select>
                      <MultiSelectDropdownSearch
                        options={tools}
                        selected={selectedForThisServer}
                        onChange={(newSelectedTools) => {
                          setSelectedAgent((prev) => ({
                            ...prev,
                            mcp_servers: {
                              ...(prev.mcp_servers || {}),
                              [selectedMCPServer]: newSelectedTools,
                            },
                          }));
                        }}
                        placeholder={`Select tools for ${selectedMCPServer}`}
                        displayKey="name"
                        valueKey="name"
                      />
                    </>
                  );
                })()}
              </>
            )}


          </>
        )}

        {/* Step 3: Prompts */}
        {currentSetupStep === 3 && (
          <>
            <InfoBox>
              Configure the prompts that define your agent's behavior. Well-crafted prompts are essential for optimal performance.
            </InfoBox>

            <TabsContainer>
              <ModalTab
                active={modalTabPrompt === 'system'}
                onClick={() => setModalTabPrompt('system')}
              >
                System Prompt
              </ModalTab>
              <ModalTab
                active={modalTabPrompt === 'examples'}
                onClick={() => setModalTabPrompt('examples')}
              >
                Example Conversations
              </ModalTab>
              <ModalTab
                active={modalTabPrompt === 'templates'}
                onClick={() => setModalTabPrompt('templates')}
              >
                Response Templates
              </ModalTab>
            </TabsContainer>

            {modalTabPrompt === 'system' && (
              <>
                <FormGroup>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Label>System Prompt</Label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button
                        onClick={() => setShowPromptTips(!showPromptTips)}
                        style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                      >
                        Load Template
                      </Button>
                      <Button
                        onClick={() => updateSelectedAgent("prompt.system_prompt", '')}
                        style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>

                  {showPromptTips ? (
                    <div style={{
                      background: 'rgba(0, 102, 255, 0.05)',
                      padding: '1rem',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                      fontSize: '0.9rem'
                    }}>
                      <Table>
                        <thead>
                          <th>Prompt Title</th>
                          <th>Action</th>
                        </thead>
                        <tbody>
                          {promptList.map((singlePrompt, index) => (
                            <TableRow key={index}>
                              <TableCell>{singlePrompt.name}</TableCell>
                              <TableCell>
                                <Button
                                  outline
                                  onClick={() => {
                                    updateSelectedAgent("prompt.system_prompt", singlePrompt.prompt);
                                    setShowPromptTips(!showPromptTips);
                                  }}
                                >
                                  Select
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </tbody>
                      </Table>
                      <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>Tips for effective system prompts:</div>
                      <ul style={{ paddingLeft: '1.5rem', margin: '0' }}>
                        <li>Be specific about the agent's role and responsibilities</li>
                        <li>Define the tone and style of communication</li>
                        <li>Specify any constraints or limitations</li>
                        <li>Include domain-specific terminology and concepts</li>
                        <li>Use clear, concise language</li>
                      </ul>
                    </div>
                  ) : (
                    <>
                      <Textarea
                        placeholder="Enter system prompt"
                        value={systemPrompt}
                        onChange={e => setSystemPrompt(e.target.value)}
                        onBlur={() => updateSelectedAgent("prompt.system_prompt", systemPrompt)}
                        style={{ minHeight: '200px', fontFamily: 'monospace' }}
                      />
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.8rem',
                        color: 'var(--text-secondary)',
                        marginTop: '0.5rem'
                      }}>
                        <span>Tokens: ~{Math.round(systemPrompt.length / 4)}</span>
                        <span>{systemPrompt.length} characters</span>
                      </div>
                    </>
                  )}

                </FormGroup>

                <FormGroup>
                  <Label>Variables</Label>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '0.5rem',
                    marginBottom: '1rem'
                  }}>
                    {['{{user_name}}', '{{company_name}}', '{{current_date}}', '{{policy_number}}', '{{agent_name}}'].map(variable => (
                      <div
                        key={variable}
                        onClick={() => {
                          const textarea = document.querySelector('textarea');
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const newValue = systemPrompt.substring(0, start) + variable + systemPrompt.substring(end);
                          setSystemPrompt(newValue);
                        }}
                        style={{
                          padding: '0.5rem',
                          background: 'rgba(0, 102, 255, 0.1)',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          cursor: 'pointer',
                          textAlign: 'center'
                        }}
                      >
                        {variable}
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    Click on a variable to insert it at the cursor position in the system prompt
                  </div>
                </FormGroup>
              </>
            )}

            {modalTabPrompt === 'examples' && (
              <>
                <FormGroup>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <Label style={{ margin: 0 }}>Example Conversations</Label>
                    <Button onClick={() => handleAddExampleConversation()}>Add Example</Button>
                  </div>

                  {selectedAgent.prompt.example_conversation.length === 0 ? (
                    <div style={{
                      padding: '2rem',
                      textAlign: 'center',
                      background: 'rgba(0, 102, 255, 0.02)',
                      borderRadius: '8px',
                      border: '1px dashed rgba(0, 102, 255, 0.2)'
                    }}>
                      <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>📝</div>
                      <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>No examples yet</div>
                      <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                        Add example conversations to help your agent understand the expected interactions
                      </div>
                    </div>
                  ) : (
                    <div>
                      {(selectedAgent?.prompt?.example_conversation || []).map((example, index) => (
                        <div
                          key={index}
                          style={{
                            marginBottom: '1.5rem',
                            background: 'rgba(0, 102, 255, 0.02)',
                            borderRadius: '8px',
                            border: '1px solid rgba(0, 102, 255, 0.1)',
                            padding: '1rem'
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '1rem'
                          }}>
                            <div style={{ fontWeight: '500' }}>{example.title}</div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <Button
                                onClick={() => handleEditExampleConversation(index)}
                                style={{ padding: '0.5rem', fontSize: '0.8rem' }}
                              >
                                Edit
                              </Button>
                              <Button
                                onClick={() => handleRemoveExampleConversation(index)}
                                style={{ padding: '0.5rem', fontSize: '0.8rem', background: 'var(--danger)' }}
                              >
                                Remove
                              </Button>
                            </div>
                          </div>

                          {example.conversations.map((message, msgIndex) => (
                            <div
                              key={msgIndex}
                              style={{
                                padding: '0.75rem',
                                background: message.role === 'user' ? 'rgba(0, 102, 255, 0.05)' : 'white',
                                borderRadius: '8px',
                                marginBottom: '0.5rem',
                                border: message.role === 'user' ? 'none' : '1px solid rgba(0, 102, 255, 0.1)'
                              }}
                            >
                              <div style={{
                                fontWeight: '500',
                                marginBottom: '0.5rem',
                                color: message.role === 'user' ? 'var(--primary)' : 'var(--text-primary)'
                              }}>
                                {message.role === 'user' ? 'User' : 'Agent'}:
                              </div>
                              <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </FormGroup>

                {showExampleEditor && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                  }}>
                    <div style={{
                      background: 'white',
                      borderRadius: '8px',
                      padding: '1.5rem',
                      width: '80%',
                      maxWidth: '800px',
                      maxHeight: '80vh',
                      overflow: 'auto'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '1.5rem'
                      }}>
                        <SubSectionTitle style={{ margin: 0 }}>
                          {editingExampleIndex === -1 ? 'Add Example Conversation' : 'Edit Example Conversation'}
                        </SubSectionTitle>
                        <Button onClick={() => setShowExampleEditor(false)}>Close</Button>
                      </div>
                      <FormGroup>
                        <FormRow>
                          <Label>Title</Label>
                          <Input type="text" value={currentExampleMessages.title} onChange={(e) => {
                            setCurrentExampleMessages((prev) => ({
                              ...prev,
                              title: e.target.value,
                            }));
                          }} />
                        </FormRow>
                      </FormGroup>
                      <div style={{ marginBottom: '1.5rem' }}>
                        {currentExampleMessages.conversations.map((message, index) => (
                          <div
                            key={index}
                            style={{
                              marginBottom: '1rem',
                              background: 'rgba(0, 102, 255, 0.02)',
                              borderRadius: '8px',
                              padding: '1rem'
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '0.5rem'
                            }}>
                              <Select
                                value={message.role}
                                onChange={e => handleUpdateExampleMessage(index, 'role', e.target.value)}
                                style={{ width: '150px' }}
                              >
                                <option value="user">User</option>
                                <option value="assistant">Assistant</option>
                                <option value="system">System</option>
                              </Select>
                              <Button
                                onClick={() => handleRemoveExampleMessage(index)}
                                style={{ padding: '0.5rem', fontSize: '0.8rem', background: 'var(--danger)' }}
                              >
                                Remove
                              </Button>
                            </div>
                            <Textarea
                              value={message.content}
                              onChange={e => handleUpdateExampleMessage(index, 'content', e.target.value)}
                              placeholder={`Enter ${message.role} message`}
                              style={{ minHeight: '100px' }}
                            />
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <Button onClick={handleAddExampleMessage} style={{ padding: '0.5rem 1rem', fontSize: '0.8rem' }}>Add Message</Button>
                        <ButtonSuccess
                          onClick={handleSaveExampleConversation}
                          style={{ padding: '0.5rem 1rem', fontSize: '0.8rem' }}
                        >
                          Save Conversation
                        </ButtonSuccess>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {modalTabPrompt === 'templates' && (
              <>
                <FormGroup>
                  <Label>Response Templates</Label>
                  <div style={{ marginBottom: '1rem' }}>
                    <Select
                      value={selectedAgent.prompt.response_template.template_type}
                      onChange={e => {
                        const selectedOption = e.target.options[e.target.selectedIndex].text;

                        updateSelectedAgent("prompt.response_template.template_type", e.target.value)
                        updateSelectedAgent("prompt.response_template.template_name", selectedOption)

                      }}
                    >
                      <option value="">Select template type</option>
                      <option value="greeting">Greeting</option>
                      <option value="error">Error Response</option>
                      <option value="clarification">Clarification Request</option>
                      <option value="confirmation">Confirmation</option>
                      <option value="custom">Custom Template</option>
                    </Select>
                  </div>

                  {selectedAgent.prompt.response_template.template_type && (
                    <div style={{
                      background: 'rgba(0, 102, 255, 0.02)',
                      borderRadius: '8px',
                      border: '1px solid rgba(0, 102, 255, 0.1)',
                      padding: '1.5rem',
                      marginBottom: '1.5rem'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '1rem'
                      }}>
                        <div style={{ fontWeight: '500' }}>
                          {(selectedAgent.prompt.response_template.template_name)}
                        </div>
                        <Button
                          onClick={() => setTemplateContent(defaultTemplates[selectedTemplate] || '')}
                          style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                        >
                          Reset to Default
                        </Button>
                      </div>

                      <Textarea
                        value={selectedAgent.prompt.response_template.template_content}
                        onChange={e => updateSelectedAgent("prompt.response_template.template_content", e.target.value)}
                        placeholder="Enter template content"
                        style={{ minHeight: '150px' }}
                      />

                      <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginTop: '1rem'
                      }}>
                        <Button onClick={handleSaveTemplate}>Save Template</Button>
                      </div>
                    </div>
                  )}
                  {/* TODO */}
                  {/* <FormGroup>
                    <Label>Variables</Label>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '0.5rem',
                      marginBottom: '1rem'
                    }}>
                      {['{{user_name}}', '{{company_name}}', '{{current_date}}', '{{policy_number}}', '{{agent_name}}'].map(variable => (
                        <div
                          key={variable}
                          onClick={() => {
                            const textarea = document.querySelector('textarea');
                            const start = textarea.selectionStart;
                            const end = textarea.selectionEnd;
                            const newValue = systemPrompt.substring(0, start) + variable + systemPrompt.substring(end);
                            setSystemPrompt(newValue);
                          }}
                          style={{
                            padding: '0.5rem',
                            background: 'rgba(0, 102, 255, 0.1)',
                            borderRadius: '4px',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            textAlign: 'center'
                          }}
                        >
                          {variable}
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      Click on a variable to insert it at the cursor position in the system prompt
                    </div>
                  </FormGroup> */}
                  {/* <div style={{
                    padding: '1rem',
                    background: 'rgba(0, 102, 255, 0.05)',
                    borderRadius: '8px'
                  }}>
                    <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>Available Templates:</div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '0.5rem'
                    }}>
                      {Object.keys(savedTemplates).map(template => (
                        <div
                          key={template}
                          style={{
                            padding: '0.75rem',
                            background: 'white',
                            borderRadius: '8px',
                            border: '1px solid rgba(0, 102, 255, 0.1)',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                          onClick={() => {
                            setSelectedTemplate(template);
                            setTemplateContent(savedTemplates[template]);
                          }}
                        >
                          <div>{template}</div>
                          <div
                            onClick={e => {
                              e.stopPropagation();
                              handleRemoveTemplate(template);
                            }}
                            style={{ color: 'var(--danger)' }}
                          >
                            ×
                          </div>
                        </div>
                      ))}
                    </div>
                  </div> */}
                </FormGroup>
              </>
            )}
          </>
        )}

        {/* Step 4: Security & Guardrails */}
        {currentSetupStep === 4 && (
          <>
            <InfoBox>
              Configure security settings and guardrails to ensure your agent operates safely and within appropriate boundaries.
            </InfoBox>

            <TabsContainer>
              <ModalTab
                active={modalTabSecurity === 'security'}
                onClick={() => setModalTabSecurity('security')}
              >
                Security Settings
              </ModalTab>
              <ModalTab
                active={modalTabSecurity === 'guardrails'}
                onClick={() => setModalTabSecurity('guardrails')}
              >
                Content Guardrails
              </ModalTab>
              <ModalTab
                active={modalTabSecurity === 'access'}
                onClick={() => setModalTabSecurity('access')}
              >
                Access Control
              </ModalTab>
            </TabsContainer>
            {modalTabSecurity === 'security' && (
              <>
                <FormGroup>
                  <Label>Security Policies</Label>
                  <div style={{
                    background: 'rgba(0, 102, 255, 0.02)',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 102, 255, 0.1)',
                    padding: '1rem',
                    marginBottom: '1.5rem'
                  }}>
                    {[
                      { id: 'data_encryption', name: 'Data Encryption', description: 'Encrypt all data at rest and in transit' },
                      { id: 'pii_handling', name: 'PII Handling', description: 'Special handling for personally identifiable information' },
                      { id: 'audit_loggin', name: 'Audit Logging', description: 'Log all agent actions for audit purposes' },
                      { id: 'input_validation', name: 'Input Validation', description: 'Validate all inputs to prevent injection attacks' },
                      { id: 'rate_limiting', name: 'Rate Limiting', description: 'Limit the number of requests to prevent abuse' }
                    ].map(policy => (
                      <div
                        key={policy.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.75rem',
                          borderBottom: '1px solid rgba(0, 102, 255, 0.1)',
                          gap: '1rem'
                        }}
                      >
                        <ToggleSwitch
                          on={selectedAgent.security.security_settings[policy.id]}
                          onClick={() => {
                            let path = "security.security_settings." + policy.id;
                            const newValue = !selectedAgent.security.security_settings[policy.id]; // Toggle the current value
                            updateSelectedAgent(path, newValue);
                          }}
                        />
                        <div>
                          <div style={{ fontWeight: '500' }}>{policy.name}</div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{policy.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>Authentication Method</Label>
                  <CardGrid style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                    {[
                      { id: 'api_key', name: 'API Key', icon: '🔑', description: 'Simple API key authentication' },
                      { id: 'oauth', name: 'OAuth 2.0', icon: '🔐', description: 'Industry standard authorization framework' },
                      { id: 'jwt', name: 'JWT', icon: '🎫', description: 'JSON Web Token based authentication' }
                    ].map(method => (
                      <SelectableCard
                        key={method.id}
                        selected={selectedAgent.security.security_settings.authentication_method === method.id}
                        onClick={() => updateSelectedAgent('security.security_settings.authentication_method', method.id)}
                      >
                        <CardIcon selected={authMethod === method.id}>{method.icon}</CardIcon>
                        <CardTitle selected={authMethod === method.id}>{method.name}</CardTitle>
                        <CardDescription>{method.description}</CardDescription>
                      </SelectableCard>
                    ))}
                  </CardGrid>
                </FormGroup>

                <FormGroup>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <Label style={{ margin: 0 }}>API Rate Limiting</Label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Enable Rate Limiting</span>
                      

                      <ToggleSwitch
                        on={selectedAgent.security.security_settings.api_rate_limiting}
                        onClick={() => {
                          let path = "security.security_settings.api_rate_limiting";
                          const newValue = !selectedAgent.security.security_settings.api_rate_limiting;
                          updateSelectedAgent(path, newValue);
                        }}
                      />


                    </div>
                  </div>

                  {selectedAgent.security.security_settings.api_rate_limiting && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '1rem',
                      background: 'rgba(0, 102, 255, 0.02)',
                      padding: '1rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(0, 102, 255, 0.1)'
                    }}>
                      <div>
                        <Label style={{ fontSize: '0.9rem' }}>Requests per Minute</Label>
                        <Input
                          type="number"
                          min="1"
                          max="1000"
                          value={selectedAgent.security.security_settings.api_limit_requests_per_minute}
                          onChange={e => updateSelectedAgent("security.security_settings.api_limit_requests_per_minute", parseInt(e.target.value))}
                        />
                      </div>
                      <div>
                        <Label style={{ fontSize: '0.9rem' }}>Burst Limit</Label>
                        <Input
                          type="number"
                          min="1"
                          max="100"
                          value={selectedAgent.security.security_settings.api_limit_burst_limit}
                          onChange={e => updateSelectedAgent("security.security_settings.api_limit_burst_limit", parseInt(e.target.value))}
                        />
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                          Maximum number of requests allowed in a burst
                        </div>
                      </div>
                    </div>
                  )}
                </FormGroup>
              </>
            )}

            {modalTabSecurity === 'guardrails' && (
              <>
                <FormGroup>
                  <Label>Content Filtering</Label>
                  <div style={{
                    background: 'rgba(0, 102, 255, 0.02)',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 102, 255, 0.1)',
                    padding: '1rem',
                    marginBottom: '1.5rem'
                  }}>
                    {[
                      { id: 'profanity_filter', name: 'Profanity Filter', description: 'Filter out profane language' },
                      { id: 'pii_detection', name: 'PII Detection', description: 'Detect and mask personally identifiable information' },
                      { id: 'harmful_content', name: 'Harmful Content', description: 'Filter potentially harmful content' },
                      { id: 'misinformation', name: 'Misinformation', description: 'Detect and flag potential misinformation' },
                      { id: 'bias_detection', name: 'Bias Detection', description: 'Detect and mitigate potential bias in responses' }
                    ].map(filter => (
                      <div
                        key={filter.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.75rem',
                          borderBottom: '1px solid rgba(0, 102, 255, 0.1)',
                          gap: '1rem'
                        }}
                      >
                        <ToggleSwitch
                          on={selectedAgent.security.security_settings[filter.id]}
                          onClick={() => {
                            let path = "security.security_settings." + filter.id;
                            const newValue = !selectedAgent.security.security_settings[filter.id]; // Toggle the current value
                            updateSelectedAgent(path, newValue);
                          }}
                        />
                        <div>
                          <div style={{ fontWeight: '500' }}>{filter.name}</div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{filter.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>Content Moderation Level</Label>
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                      <div style={{ flex: '1' }}>
                        <Slider
                          type="range"
                          min="1"
                          max="5"
                          step="1"
                          value={selectedAgent.security.content_guardrails.content_moderation}
                          onChange={e => {
                            updateSelectedAgent("security.content_guardrails.content_moderation", parseInt(e.target.value))
                            setModerationLevel(parseInt(e.target.value))
                          }}
                        />
                      </div>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'var(--gradient-primary)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '600',
                        fontSize: '1.2rem'
                      }}>
                        {selectedAgent.security.content_guardrails.content_moderation}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '0.8rem',
                      color: 'var(--text-secondary)'
                    }}>
                      <span>Minimal</span>
                      <span>Balanced</span>
                      <span>Strict</span>
                    </div>
                  </div>

                  <div style={{
                    padding: '1rem',
                    background: 'rgba(0, 102, 255, 0.05)',
                    borderRadius: '8px',
                    fontSize: '0.9rem'
                  }}>
                    <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>Current Level: {
                      moderationLevel === 1 ? 'Minimal - Basic filtering only' :
                        moderationLevel === 2 ? 'Low - Light content moderation' :
                          moderationLevel === 3 ? 'Balanced - Moderate content filtering' :
                            moderationLevel === 4 ? 'High - Strict content guidelines' :
                              'Maximum - Most restrictive filtering'
                    }</div>
                    <div>
                      {moderationLevel === 1 && 'Only filters the most extreme content. Allows most language and topics.'}
                      {moderationLevel === 2 && 'Light filtering that allows most content but filters extreme cases.'}
                      {moderationLevel === 3 && 'Balanced approach that filters inappropriate content while allowing most business topics.'}
                      {moderationLevel === 4 && 'Strict filtering that prevents most potentially problematic content.'}
                      {moderationLevel === 5 && 'Maximum safety with the most restrictive content filtering.'}
                    </div>
                  </div>
                </FormGroup>
                {/* TODO */}
                {/* <FormGroup>
                  <Label>Custom Blocked Topics</Label>
                  <div style={{ display: 'flex', marginBottom: '0.5rem' }}>
                    <Input
                      type="text"
                      placeholder="Add topic to block"
                      value={newBlockedTopic}
                      onChange={e => setNewBlockedTopic(e.target.value)}
                      onKeyPress={e => e.key === 'Enter' && handleAddBlockedTopic()}
                      style={{ flex: '1', marginRight: '0.5rem' }}
                    />
                    <Button onClick={handleAddBlockedTopic}>Add</Button>
                  </div>

                  <TagsContainer>
                    {blockedTopics.map(topic => (
                      <Tag key={topic}>
                        {topic}
                        <span onClick={() => handleRemoveBlockedTopic(topic)}>×</span>
                      </Tag>
                    ))}
                  </TagsContainer>
                </FormGroup> */}
              </>
            )}

            {modalTabSecurity === 'access' && (
              <>
                <FormGroup>
                  <Label>Access Level</Label>
                  <CardGrid style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                    {[
                      { id: 'public', name: 'Public', icon: '🌐', description: 'Available to all users' },
                      { id: 'restricted', name: 'Restricted', icon: '🔒', description: 'Limited to specific user groups' },
                      { id: 'private', name: 'Private', icon: '🔐', description: 'Only available to authorized users' }
                    ].map(level => (
                      <SelectableCard
                        key={level.id}
                        selected={selectedAgent.security.access_control.access_level === level.id}
                        onClick={() => updateSelectedAgent('security.access_control.access_level', level.id)}
                      >
                        <CardIcon selected={selectedAgent.security.access_control.access_level === level.id}>{level.icon}</CardIcon>
                        <CardTitle selected={selectedAgent.security.access_control.access_level === level.id}>{level.name}</CardTitle>
                        <CardDescription>{level.description}</CardDescription>
                      </SelectableCard>
                    ))}
                  </CardGrid>
                </FormGroup>
                {/* TODO */}
                {/* {selectedAgent.security.access_control.access_level === 'restricted' && (
                  <FormGroup>
                    <Label>User Groups</Label>
                    <div style={{ display: 'flex', marginBottom: '0.5rem' }}>
                      <Input
                        type="text"
                        placeholder="Add user group"
                        value={newUserGroup}
                        onChange={e => setNewUserGroup(e.target.value)}
                        onKeyPress={e => e.key === 'Enter' && handleAddUserGroup()}
                        style={{ flex: '1', marginRight: '0.5rem' }}
                      />
                      <Button onClick={handleAddUserGroup}>Add</Button>
                    </div>

                    <TagsContainer>
                      {userGroups.map(group => (
                        <Tag key={group}>
                          {group}
                          <span onClick={() => handleRemoveUserGroup(group)}>×</span>
                        </Tag>
                      ))}
                    </TagsContainer>
                  </FormGroup>
                )} */}

                <FormGroup>
                  <Label>Data Access Permissions</Label>
                  <div style={{
                    background: 'rgba(0, 102, 255, 0.02)',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 102, 255, 0.1)',
                    padding: '1rem',
                    marginBottom: '1.5rem'
                  }}>
                    {[
                      { id: 'read_user_data', name: 'Read User Data', description: 'Access to user information' },
                      { id: 'write_user_data', name: 'Write User Data', description: 'Modify user information' },
                      { id: 'read_system_data', name: 'Read System Data', description: 'Access to system information' },
                      { id: 'write_system_data', name: 'Write System Data', description: 'Modify system information' }
                    ].map(permission => (
                      <div
                        key={permission.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.75rem',
                          borderBottom: '1px solid rgba(0, 102, 255, 0.1)',
                          gap: '1rem'
                        }}
                      >

                        <ToggleSwitch
                          on={selectedAgent.security.access_control[permission.id]}
                          onClick={() => {
                            let path = "security.security_settings." + permission.id;
                            const newValue = !selectedAgent.security.security_settings[permission.id];
                            updateSelectedAgent(path, newValue);
                          }}
                        />

                        <div>
                          <div style={{ fontWeight: '500' }}>{permission.name}</div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{permission.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>Audit Logging</Label>
                  <Select
                    value={selectedAgent.security.access_control.audit_logging}
                    onChange={e => updateSelectedAgent("security.access_control.audit_logging", e.target.value)}
                  >
                    <option value="none">None</option>
                    <option value="basic">Basic (errors only)</option>
                    <option value="standard">Standard (errors and warnings)</option>
                    <option value="detailed">Detailed (all actions)</option>
                    <option value="verbose">Verbose (all actions with full details)</option>
                  </Select>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                    Determines the level of detail in audit logs for this agent
                  </div>
                </FormGroup>
              </>
            )}
          </>
        )}

        {/* Step 5: Review */}
        {currentSetupStep === 5 && (
          <>
            <InfoBox>
              Review your agent configuration before finalizing. Ensure all settings are correct and meet your requirements.
            </InfoBox>
            <div style={{
              background: 'rgba(0, 102, 255, 0.02)',
              borderRadius: '8px',
              border: '1px solid rgba(0, 102, 255, 0.1)',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '16px',
                  background: 'var(--gradient-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2.5rem',
                  color: '#ffffff'
                }}>
                  <img src={`${selectedAgent.icon || defaultAgentIcon}`} style={{ height: "50px", width: "50px" }} />
                </div>
                <div>
                  <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>{selectedAgent.name || 'Unnamed Agent'}</h2>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                    {selectedAgent.type} • {selectedAgent.business_domain.replace(/_/g, ' ')}
                  </div>
                  <div style={{ marginTop: '0.5rem' }}>
                    {selectedAgent.tags.map(tag => (
                      <Tag key={tag}>{tag}</Tag>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ fontSize: '0.9rem', marginBottom: '1.5rem', whiteSpace: 'pre-wrap' }}>
                {selectedAgent.description}
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1.5rem',
                fontSize: '0.9rem'
              }}>
                <div>
                  <h3 style={{ fontSize: '1rem', marginTop: 0 }}>Model & Knowledge</h3>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>AI Model:</strong> {modelList.find(model => model.code === selectedAgent.general_settings.model_code)?.name || 'Unknown Model'}
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Temperature:</strong> {selectedAgent.general_settings.model_temperature}
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Max Tokens:</strong> {selectedAgent.general_settings.max_tokens}
                  </div>
                  <div>
                    <strong>Knowledge Bases:</strong> {selectedKnowledgeBase.length > 0 ? selectedKnowledgeBase.join(', ') : 'None'}
                  </div>
                </div>

                <div>
                  <h3 style={{ fontSize: '1rem', marginTop: 0 }}>Security & Access</h3>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Access Level:</strong> {accessLevel || 'Not set'}
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Authentication:</strong> {authMethod || 'Not set'}
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Content Moderation:</strong> Level {moderationLevel}
                  </div>
                  <div>
                    <strong>Security Policies:</strong> {securityPolicies.length > 0 ? securityPolicies.length + ' enabled' : 'None'}
                  </div>
                </div>
              </div>
            </div>

            <FormGroup>
              <Label>Validation Status</Label>
              <div style={{
                padding: '1rem',
                background: validationErrors.length > 0 ? 'rgba(255, 0, 0, 0.05)' : 'rgba(0, 200, 0, 0.05)',
                borderRadius: '8px',
                border: `1px solid ${validationErrors.length > 0 ? 'rgba(255, 0, 0, 0.2)' : 'rgba(0, 200, 0, 0.2)'}`,
                marginBottom: '1.5rem'
              }}>
                {validationErrors.length > 0 ? (
                  <>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: 'var(--danger)',
                      fontWeight: '500',
                      marginBottom: '0.5rem'
                    }}>
                      <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                      <span>Please fix the following issues before creating your agent:</span>
                    </div>
                    <ul style={{ margin: '0', paddingLeft: '1.5rem' }}>
                      {validationErrors.map((error, index) => (
                        <li key={index} style={{ color: 'var(--danger)' }}>{error}</li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: 'var(--success)',
                    fontWeight: '500'
                  }}>
                    <span style={{ fontSize: '1.2rem' }}>✅</span>
                    <span>All required fields are complete. Your agent is ready to be created!</span>
                  </div>
                )}
              </div>
            </FormGroup>

            <FormGroup>
              <Label>Deployment Options</Label>
              <CardGrid style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                {[
                  { id: 'development', name: 'Development', icon: '🧪', description: 'Test environment with debugging features' },
                  { id: 'staging', name: 'Staging', icon: '🔍', description: 'Pre-production environment for testing' },
                  { id: 'production', name: 'Production', icon: '🚀', description: 'Live environment for end users' }
                ].map(env => (
                  <SelectableCard
                    key={env.id}
                    selected={deploymentEnvironment === env.id}
                    onClick={() => setDeploymentEnvironment(env.id)}
                  >
                    <CardIcon selected={deploymentEnvironment === env.id}>{env.icon}</CardIcon>
                    <CardTitle selected={deploymentEnvironment === env.id}>{env.name}</CardTitle>
                    <CardDescription>{env.description}</CardDescription>
                  </SelectableCard>
                ))}
              </CardGrid>
            </FormGroup>

            <FormGroup>
              <Label>Deployment Schedule</Label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                <Select
                  value={deploymentType}
                  onChange={e => setDeploymentType(e.target.value)}
                  style={{ flex: '1' }}
                >
                  <option value="immediate">Deploy Immediately</option>
                  <option value="scheduled">Schedule Deployment</option>
                </Select>

                {deploymentType === 'scheduled' && (
                  <Input
                    type="datetime-local"
                    value={deploymentSchedule}
                    onChange={e => setDeploymentSchedule(e.target.value)}
                    style={{ flex: '1' }}
                  />
                )}
              </div>

              <div style={{
                padding: '1rem',
                background: 'rgba(0, 102, 255, 0.05)',
                borderRadius: '8px',
                fontSize: '0.9rem'
              }}>
                <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>Deployment Notes:</div>
                <ul style={{ margin: '0', paddingLeft: '1.5rem' }}>
                  <li>Development deployments are available immediately for testing</li>
                  <li>Staging and Production deployments may require additional approvals</li>
                  <li>You can modify your agent settings after deployment</li>
                </ul>
              </div>
            </FormGroup>
          </>
        )}

        {/* Modal Footer */}
        <ModalFooter>
          {currentSetupStep > 1 ? (
            <Button onClick={() => setCurrentSetupStep((prev) => prev - 1)}>Previous</Button>
          ) : <div></div>}
          {currentSetupStep < 5 && (
            <Button onClick={() => setCurrentSetupStep((prev) => prev + 1)}>Next</Button>
          )}
          {currentSetupStep === 5 && (
            <Button primary onClick={handleSubmit}>
              Create Agent
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    );
  };
  const validateAgent = () => {
    const errors = [];

    if (!agentName.trim()) {
      errors.push('Agent name is required');
    }

    if (!agentType) {
      errors.push('Agent type is required');
    }

    if (!businessDomain) {
      errors.push('Business domain is required');
    }

    if (!agentDescription.trim()) {
      errors.push('Agent description is required');
    }

    if (!selectedModel) {
      errors.push('AI model is required');
    }

    if (systemPrompt.trim().length < 50) {
      errors.push('System prompt should be more detailed (at least 50 characters)');
    }

    return errors;
  };

  const handleCreateAgent = () => {
    const errors = validateAgent();
    setValidationErrors(errors);

    if (errors.length === 0) {
      setIsCreatingAgent(true);

      // Simulate API call
      setTimeout(() => {
        const newAgent = {
          id: Date.now().toString(),
          name: agentName,
          type: agentType,
          description: agentDescription,
          icon: agentIcon,
          tags: agentTags,
          model: selectedModel,
          businessDomain: businessDomain,
          createdAt: new Date().toISOString(),
          status: 'active'
        };

        // setAgents([...agents, newAgent]);
        setIsCreatingAgent(false);
        closeModal();

        // Reset form
        setAgentName('');
        setAgentType('');
        setAgentDescription('');
        setBusinessDomain('');
        setSelectedModel('');
        setSystemPrompt('');
        setAgentIcon('/assets/images/agents/agent.png');
        setAgentTags([]);
        setExampleConversations([]);
        setSecurityPolicies([]);
        setAccessLevel('');
        setCurrentSetupStep(1);
      }, 2000);
    }
  };

  const handleEditExampleConversation = (index) => {
  setEditingExampleIndex(index);
  if (selectedAgent.prompt.example_conversation[index]) {
    setCurrentExampleMessages({
      title: selectedAgent.prompt.example_conversation[index].title || '',
      conversations: [...(selectedAgent.prompt.example_conversation[index].conversations || [])]
    });
  } else {  
    setCurrentExampleMessages({
      title: '',
      conversations: []
    });
  }
  setShowExampleEditor(true);
};


  const handleAddExampleMessage = () => {
    setCurrentExampleMessages((prev) => ({
      ...prev,
      conversations: [
        ...prev.conversations,
        { role: 'user', content: '' },
      ],
    }));
  };

 const handleRemoveExampleMessage = (index) => {
  setCurrentExampleMessages({
    ...currentExampleMessages,
    conversations: currentExampleMessages.conversations.filter((_, i) => i !== index)
  });
};

  const handleSaveExampleConversation = () => {
    setSelectedAgent((prev) => {
      const updatedExampleConversations = [...prev.prompt.example_conversation];

      if (editingExampleIndex === -1) {
        // Add a new example conversation
        updatedExampleConversations.push(currentExampleMessages);
      } else {
        // Update the existing example conversation
        updatedExampleConversations[editingExampleIndex] = currentExampleMessages;
      }

      return {
        ...prev,
        prompt: {
          ...prev.prompt,
          example_conversation: updatedExampleConversations,
        },
      };
    });

    // Reset editor state
    setShowExampleEditor(false);
    setEditingExampleIndex(-1);
    setCurrentExampleMessages({ title: '', conversations: [] });
  };

  const handleSaveTemplate = () => {
    setTemplates({
      ...templates,
      [selectedTemplate]: templateContent
    });
    setTemplateContent('');
    setSelectedTemplate('');
  };

  const handleRemoveTemplate = (templateType) => {
    const updatedTemplates = { ...templates };
    delete updatedTemplates[templateType];
    setTemplates(updatedTemplates);
  };

  const handleToggleContentFilter = (filterName) => {
    setContentFilters({
      ...contentFilters,
      [filterName]: !contentFilters[filterName]
    });
  };

  const handleAddBlockedTopic = (e) => {
    e?.preventDefault();
    if (newBlockedTopic.trim() && !blockedTopics.includes(newBlockedTopic.trim())) {
      setBlockedTopics([...blockedTopics, newBlockedTopic.trim()]);
      setNewBlockedTopic('');
    }
  };

  const handleRemoveBlockedTopic = (topic) => {
    setBlockedTopics(blockedTopics.filter(t => t !== topic));
  };

  const handleAddUserGroup = (e) => {
    e?.preventDefault();
    if (newUserGroup.trim() && !userGroups.includes(newUserGroup.trim())) {
      setUserGroups([...userGroups, newUserGroup.trim()]);
      setNewUserGroup('');
    }
  };

  const handleRemoveUserGroup = (group) => {
    setUserGroups(userGroups.filter(g => g !== group));
  };

  const handleToggleDataPermission = (permissionName) => {
    setDataPermissions({
      ...dataPermissions,
      [permissionName]: !dataPermissions[permissionName]
    });
  };

  const renderWorkingAgents = () => {
    return (
      <div style={{ marginBottom: '3rem' }}>
        <SectionTitle>Working Agents <span>{workingAgents.length}</span></SectionTitle>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(0, 102, 255, 0.1)' }}>
                <th style={{ textAlign: 'left', padding: '1rem', fontWeight: '600' }}>Agent</th>
                <th style={{ textAlign: 'left', padding: '1rem', fontWeight: '600' }}>Task</th>
                <th style={{ textAlign: 'left', padding: '1rem', fontWeight: '600' }}>Progress</th>
                <th style={{ textAlign: 'left', padding: '1rem', fontWeight: '600' }}>Start Time</th>
                <th style={{ textAlign: 'left', padding: '1rem', fontWeight: '600' }}>Time Remaining</th>
                <th style={{ textAlign: 'left', padding: '1rem', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {workingAgents.map(agent => (
                <tr key={agent.id} style={{ borderBottom: '1px solid rgba(0, 102, 255, 0.05)' }}>
                  <td style={{ padding: '1rem', display: 'flex', alignItems: 'center' }}>
                    <AgentIcon active>
                      <img src={`assets/images/agents/${agent.icon}`} style={{ width: '36px', height: '36px', fontSize: '1.2rem' }} />
                    </AgentIcon>
                    <div style={{ marginLeft: '0.5rem' }}>
                      <div style={{ fontWeight: '500' }}>{agent.name}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        {agent.type}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>{agent.task}</td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ width: '100%', background: 'rgba(0, 102, 255, 0.1)', height: '8px', borderRadius: '4px' }}>
                      <div
                        style={{
                          width: `${agent.progress}%`,
                          background: 'var(--primary)',
                          height: '8px',
                          borderRadius: '4px'
                        }}
                      />
                    </div>
                    <div style={{ fontSize: '0.8rem', marginTop: '0.3rem', textAlign: 'right' }}>{agent.progress}%</div>
                  </td>
                  <td style={{ padding: '1rem' }}>{agent.startTime}</td>
                  <td style={{ padding: '1rem' }}>{agent.timeRemaining}</td>
                  <td style={{ padding: '1rem' }}>
                    <ButtonGroup>
                      <Button style={{ padding: '0.5rem' }}>Pause</Button>
                      <Button style={{ padding: '0.5rem' }}>Stop</Button>
                    </ButtonGroup>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <Container>
      <PageHeaderWithInfo
        icon={faChalkboardTeacher}
        title="Agent Studio"
        description="Design, manage, and monitor your AI agents from a single dashboard"
      >
        {/* <Button primary>Report Suspicious Activity</Button> */}
        <div style={{ display: "flex", gap: "1rem" }}>
          <SearchBar>
            <SearchInput 
              placeholder="Search agents..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchBar>
          <Button primary onClick={() => openModal('setup')}>
            <span style={{ marginRight: '0.5rem' }}>+</span> Design Agent
          </Button>
        </div>
      </PageHeaderWithInfo>

      {/* Overall Metrics */}
      {/* <MetricsGrid>
        {metrics.map((metric, index) => (
          <MetricCard key={index}>
            <MetricValue>{metric.value}</MetricValue>
            <MetricLabel>{metric.label}</MetricLabel>
          </MetricCard>
        ))}
      </MetricsGrid> */}

      {/* Working Agents */}
      {/* {renderWorkingAgents()} */}

      {/* Tabs and Search */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        {/* <Tabs>
          <Tab active={activeTab === 'active'} onClick={() => setActiveTab('active')}>Active</Tab>
          <Tab active={activeTab === 'inactive'} onClick={() => setActiveTab('inactive')}>Inactive</Tab>
          <Tab active={activeTab === 'templates'} onClick={() => setActiveTab('templates')}>Templates</Tab>
        </Tabs> */}
        {/* <div style={{ display: "flex", gap: "1rem" }}>
          <SearchBar>
            <SearchInput 
              placeholder="Search agents..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchBar>
          <Button primary onClick={() => openModal('setup')}>
            <span style={{ marginRight: '0.5rem' }}>+</span> Design Agent
          </Button>
        </div> */}
      </div>


      <SectionTitle>Active Agents <span>{agentsCount}</span></SectionTitle>
      <Grid>
        {filteredAgentList.map(agent => (
          <AgentCard key={agent?.id} whileHover={{ y: -5 }}>
            <AgentHeader>
              <AgentIcon active><img src={`${agent?.icon || defaultAgentIcon}`} style={{ width: '50px', height: '50px' }} /></AgentIcon>
              <AgentInfo>
                <AgentName>{agent?.name}</AgentName>
                <AgentType>{agent?.type}</AgentType>
              </AgentInfo>
            </AgentHeader>
            <AgentStatus active={agent?.active}>
              {agent?.status === 'ACTIVE' ? 'Active' : 'Inactive'}
            </AgentStatus>
            <AgentDescription>{agent?.description}</AgentDescription>
            {/* <AgentMetrics>
              <AgentMetric>
                <AgentMetricValue>{agent.tasks}</AgentMetricValue>
                <AgentMetricLabel>Tasks</AgentMetricLabel>
              </AgentMetric>
              <AgentMetric>
                <AgentMetricValue>{agent.accuracy}</AgentMetricValue>
                <AgentMetricLabel>Accuracy</AgentMetricLabel>
              </AgentMetric>
              <AgentMetric>
                <AgentMetricValue>{agent.uptime}</AgentMetricValue>
                <AgentMetricLabel>Uptime</AgentMetricLabel>
              </AgentMetric>
            </AgentMetrics> */}
            <ButtonGroup>
              <Button onClick={() => openModal('setup', agent)}>Edit</Button>
              <Button>Deactivate</Button>
            </ButtonGroup>
          </AgentCard>
        ))}
      </Grid>

      <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalItems}
          setCurrentPage={setCurrentPage}
          setTotalPages={setTotalPages}
      />
      {/* Active Agents */}
      {/* {(activeTab === 'active') && (
        <>
          <SectionTitle>Active Agents <span>{activeAgents.length}</span></SectionTitle>
          <Grid>
            {activeAgents.map(agent => (
              <AgentCard key={agent.id} whileHover={{ y: -5 }}>
                <AgentHeader>
                  <AgentIcon active>{agent.icon}</AgentIcon>
                  <AgentInfo>
                    <AgentName>{agent.name}</AgentName>
                    <AgentType>{agent.type}</AgentType>
                  </AgentInfo>
                </AgentHeader>
                <AgentStatus active={agent.active}>
                  {agent.active ? 'Active' : 'Inactive'}
                </AgentStatus>
                <AgentDescription>{agent.description}</AgentDescription>
                <AgentMetrics>
                  <AgentMetric>
                    <AgentMetricValue>{agent.tasks}</AgentMetricValue>
                    <AgentMetricLabel>Tasks</AgentMetricLabel>
                  </AgentMetric>
                  <AgentMetric>
                    <AgentMetricValue>{agent.accuracy}</AgentMetricValue>
                    <AgentMetricLabel>Accuracy</AgentMetricLabel>
                  </AgentMetric>
                  <AgentMetric>
                    <AgentMetricValue>{agent.uptime}</AgentMetricValue>
                    <AgentMetricLabel>Uptime</AgentMetricLabel>
                  </AgentMetric>
                </AgentMetrics>
                <ButtonGroup>
                  <Button onClick={() => openModal('setup', agent)}>Edit</Button>
                  <Button>Deactivate</Button>
                </ButtonGroup>
              </AgentCard>
            ))}
          </Grid>
        </>
      )} */}

      {/* Inactive Agents */}
      {/* {(activeTab === 'inactive') && (
        <>
          <SectionTitle>Inactive Agents <span>{inactiveAgents.length}</span></SectionTitle>
          <Grid>
            {inactiveAgents.map(agent => (
              <AgentCard key={agent.id} whileHover={{ y: -5 }}>
                <AgentHeader>
                  <AgentIcon active={false}>{agent.icon}</AgentIcon>
                  <AgentInfo>
                    <AgentName>{agent.name}</AgentName>
                    <AgentType>{agent.type}</AgentType>
                  </AgentInfo>
                </AgentHeader>
                <AgentStatus active={agent.active}>
                  {agent.active ? 'Active' : 'Inactive'}
                </AgentStatus>
                <AgentDescription>{agent.description}</AgentDescription>
                <AgentMetrics>
                  <AgentMetric>
                    <AgentMetricValue>{agent.tasks}</AgentMetricValue>
                    <AgentMetricLabel>Tasks</AgentMetricLabel>
                  </AgentMetric>
                  <AgentMetric>
                    <AgentMetricValue>{agent.accuracy}</AgentMetricValue>
                    <AgentMetricLabel>Accuracy</AgentMetricLabel>
                  </AgentMetric>
                  <AgentMetric>
                    <AgentMetricValue>{agent.uptime}</AgentMetricValue>
                    <AgentMetricLabel>Uptime</AgentMetricLabel>
                  </AgentMetric>
                </AgentMetrics>
                <ButtonGroup>
                  <Button onClick={() => openModal('setup', agent)}>Edit</Button>
                  <Button primary>Activate</Button>
                </ButtonGroup>
              </AgentCard>
            ))}
          </Grid>
        </>
      )} */}

      {/* Template Agents */}
      {/* {(activeTab === 'templates') && (
        <>
          <SectionTitle>Template Agents <span>{templateAgents.length}</span></SectionTitle>
          <Grid>
            {templateAgents.map(agent => (
              <AgentCard key={agent.id} whileHover={{ y: -5 }}>
                <AgentHeader>
                  <AgentIcon active={false}>{agent.icon}</AgentIcon>
                  <AgentInfo>
                    <AgentName>{agent.name}</AgentName>
                    <AgentType>{agent.type}</AgentType>
                  </AgentInfo>
                </AgentHeader>
                <AgentDescription>{agent.description}</AgentDescription>
                <ButtonGroup>
                  <Button onClick={() => openModal('setup', { ...agent, name: `New ${agent.name}` })}>
                    Use Template
                  </Button>
                </ButtonGroup>
              </AgentCard>
            ))}
          </Grid>
        </>
      )} */}

      {/* Modal */}
      {showModal && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeModal}
        >
          <div onClick={e => e.stopPropagation()}>
            {modalType === 'setup' && renderAgentSetupModal()}
          </div>
        </Modal>
      )}
    </Container>
  );
};

export default AgentStudio;
